import { Component, OnInit } from '@angular/core';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { MESES } from 'src/app/utils/calendario-mes';
import { guardarArchivo } from 'src/app/utils/descargar';

/**
 * CALENDARIO DE LIQUIDEZ (9.4; Mario eligió la propuesta «A + C» el 09-oct): por semana, mes o año, hacia atrás y hacia
 * adelante, visto como gráfico o como lista. Lo monta la pestaña Calendario de Consultas.
 *   · Lo pasado (antes de hoy): lo que vencía y si se pagó el mismo día (FLU-06, FLU-09), de los cierres.
 *   · Lo que viene (desde hoy): lo firme y, aparte, la tendencia (FLU-10), hasta 3 meses (FLU-12). Más allá, nada.
 *   · Semana y mes, un día hábil por barra o fila; año, un mes por barra o fila (tocar un mes lo abre).
 */
type Escala = 'semana' | 'mes' | 'anio';

@Component({
  selector: 'app-liquidacion-calendario',
  templateUrl: './liquidacion-calendario.component.html'
})
export class LiquidacionCalendarioComponent implements OnInit {
  escala: Escala = 'mes';
  forma: 'grafico' | 'lista' = 'grafico';
  ancla = '';               // un día dentro del período que se ve
  hoy = '';
  cargando = false;
  error = '';
  viene: any = null;        // los próximos 66 días hábiles (se lee una vez)
  va: any[] = [];           // los días pasados del período
  vaResumen: any = null;
  elegido: string | null = null;
  abierto: string | null = null;
  private feriados = new Map<string, any>();
  private aniosFeriados = new Set<number>();

  constructor(private liq: LiquidacionService, private merchants: MerchantService) { }

  async ngOnInit(): Promise<void> {
    this.hoy = new Date(Date.now() - 4 * 3600e3).toISOString().slice(0, 10);
    this.ancla = this.hoy;
    await this.leer();
  }

  // ── fechas ──
  private sumar(d: string, n: number): string { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); }
  private dow(d: string): number { return new Date(`${d}T12:00:00Z`).getUTCDay(); }
  esHabil = (d: string): boolean => this.dow(d) !== 0 && this.dow(d) !== 6 && !this.feriados.has(d);
  feriado = (d: string): any => this.feriados.get(d) || null;
  get desde(): string {
    if (this.escala === 'semana') return this.sumar(this.ancla, -((this.dow(this.ancla) + 6) % 7));
    if (this.escala === 'mes') return `${this.ancla.slice(0, 7)}-01`;
    return `${this.ancla.slice(0, 4)}-01-01`;
  }
  get hasta(): string {
    if (this.escala === 'semana') return this.sumar(this.desde, 6);
    if (this.escala === 'mes') { const d = new Date(`${this.desde}T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + 1); d.setUTCDate(0); return d.toISOString().slice(0, 10); }
    return `${this.ancla.slice(0, 4)}-12-31`;
  }
  get titulo(): string {
    const f = (d: string) => `${+d.slice(8, 10)} ${MESES[+d.slice(5, 7) - 1].slice(0, 3)}`;
    if (this.escala === 'semana') return `Semana del ${f(this.desde)} al ${f(this.hasta)} ${this.hasta.slice(0, 4)}`;
    if (this.escala === 'mes') return `${MESES[+this.desde.slice(5, 7) - 1]} ${this.desde.slice(0, 4)}`;
    return this.desde.slice(0, 4);
  }
  get contieneHoy(): boolean { return this.desde <= this.hoy && this.hoy <= this.hasta; }
  get horizonte(): string { const ds = this.viene ? this.viene.dias : []; return ds.length ? ds[ds.length - 1].dia : this.hoy; }

  mover = (n: number): void => {
    const d = new Date(`${this.ancla}T12:00:00Z`);
    if (this.escala === 'semana') d.setUTCDate(d.getUTCDate() + 7 * n);
    else if (this.escala === 'mes') { d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + n); }
    else { d.setUTCDate(1); d.setUTCFullYear(d.getUTCFullYear() + n); }
    this.ancla = d.toISOString().slice(0, 10); this.elegido = null; this.abierto = null; this.leer();
  };
  irAHoy = (): void => { this.ancla = this.hoy; this.elegido = null; this.leer(); };
  verEscala = (e: Escala): void => { this.escala = e; this.elegido = null; this.abierto = null; this.leer(); };
  abrirMes = (mes: string): void => { this.escala = 'mes'; this.ancla = `${mes}-01`; this.elegido = null; this.leer(); };

  private async cargarFeriados(): Promise<void> {
    for (const a of new Set([+this.desde.slice(0, 4), +this.hasta.slice(0, 4), +this.hoy.slice(0, 4)])) {
      if (this.aniosFeriados.has(a)) continue;
      this.aniosFeriados.add(a);
      try { for (const f of await this.merchants.getHolidays(a)) this.feriados.set(f.date, f); } catch { this.aniosFeriados.delete(a); }
    }
  }

  /** Lo pasado del período va hasta ayer; si el período es todo futuro, se pide solo ayer (no se usa). */
  private get rangoPasado(): { desde: string; hasta: string } {
    const ayer = this.sumar(this.hoy, -1);
    const hasta = this.hasta < ayer ? this.hasta : ayer;
    return { desde: this.desde <= hasta ? this.desde : hasta, hasta };
  }

  /** Lo que viene se lee una vez (66 días hábiles, FLU-12); lo pasado, por período (hasta ayer). */
  leer = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try {
      await this.cargarFeriados();
      const p = this.rangoPasado;
      const r = await this.liq.calendario({ dias: 66, desde: p.desde, hasta: p.hasta });
      if (!this.viene) this.viene = r.viene;
      const ayer = this.sumar(this.hoy, -1);
      this.va = this.desde <= ayer ? r.va.dias : [];
      this.vaResumen = this.desde <= ayer ? r.va.resumen : null;
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo leer el calendario.'; }
    finally { this.cargando = false; }
  };

  /** FLU-07: el flujo de caja en Excel, con lo que viene y lo pasado del período que se ve. */
  excel = async (): Promise<void> => {
    try {
      const p = this.rangoPasado;
      const blob = await this.liq.excelCalendario({ dias: 66, desde: p.desde, hasta: p.hasta });
      guardarArchivo(blob, `flujo-de-caja-liquidacion-${this.desde}-a-${this.hasta}.xlsx`);
    } catch (e: any) { this.error = 'No se pudo descargar el Excel del flujo de caja.'; }
  };

  // ── formato ──
  dl = (iso: string): string => {
    const d = new Date(`${iso}T12:00:00Z`);
    return `${['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getUTCDay()]} ${d.getUTCDate()}-${['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][d.getUTCMonth()]}`;
  };
  fecha = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  origenesDe = (porOrigen: any): any[] => Object.entries(porOrigen || {}).map(([o, m]) => ({ o, m }));

  // ── los tramos del período: días hábiles (semana, mes) o meses (año) ──
  get tramos(): any[] {
    if (!this.viene) return [];
    const futuros = new Map(this.viene.dias.map((d: any) => [d.dia, d]));
    const pasados = new Map(this.va.map((g: any) => [g.dia, g]));
    if (this.escala !== 'anio') {
      const out: any[] = [];
      let saltados: any[] = [];   // FLU-02: los feriados de lunes a viernes se pagan el hábil siguiente; se dicen ahí
      for (let d = this.desde; d <= this.hasta; d = this.sumar(d, 1)) {
        if (!this.esHabil(d)) { if (this.feriado(d) && this.dow(d) !== 0 && this.dow(d) !== 6) saltados.push(this.feriado(d)); continue; }
        const base = { k: d, feriados: saltados };
        saltados = [];
        out.push(d < this.hoy ? { ...base, tipo: 'pasado', g: pasados.get(d) || null } : { ...base, tipo: d === this.hoy ? 'hoy' : 'viene', d: futuros.get(d) || null, fuera: d > this.horizonte });
      }
      return out;
    }
    const anio = this.desde.slice(0, 4);
    return Array.from({ length: 12 }, (_, i) => {
      const mes = `${anio}-${String(i + 1).padStart(2, '0')}`;
      const gs = this.va.filter((g: any) => g.dia.startsWith(mes));
      const ds = this.viene.dias.filter((d: any) => d.dia.startsWith(mes));
      const s = (xs: any[], k: string) => Math.round(xs.reduce((t, x) => t + (Number(x[k]) || 0), 0) * 100) / 100;
      return { k: mes, tipo: 'mes', mes: MESES[i], pasado: gs.length ? { vencia: s(gs, 'vencia'), a_tiempo: s(gs, 'a_tiempo'), atrasado: s(gs, 'atrasado'), no_procesado: s(gs, 'no_procesado'), a_manana: s(gs, 'a_manana') } : null,
        viene: ds.length ? { firme: s(ds, 'firme'), estimado: s(ds, 'estimado'), firme_bs: s(ds, 'firme_bs'), sin_lote: s(ds, 'sin_lote') } : null,
        fuera: `${mes}-01` > this.horizonte, antes: `${mes}-31` < this.hoy };
    });
  }

  // ── cifras del período ──
  private s = (xs: any[], k: string): number => Math.round(xs.reduce((t, x) => t + (Number(x && x[k]) || 0), 0) * 100) / 100;
  private get futurosDelPeriodo(): any[] { return this.viene ? this.viene.dias.filter((d: any) => d.dia >= this.desde && d.dia <= this.hasta) : []; }
  get firme(): number { return this.s(this.futurosDelPeriodo, 'firme'); }
  get firmeBs(): number { return this.s(this.futurosDelPeriodo, 'firme_bs'); }
  get sinLote(): number { return this.s(this.futurosDelPeriodo, 'sin_lote'); }
  get tendencia(): number { return this.s(this.futurosDelPeriodo, 'estimado'); }
  get vencia(): number { return this.s(this.va, 'vencia'); }
  get aTiempo(): number { return this.s(this.va, 'a_tiempo'); }
  get pctATiempo(): number | null { return this.vencia ? Math.round(this.aTiempo / this.vencia * 1000) / 10 : null; }
  get hayFuturo(): boolean { return this.hasta >= this.hoy; }
  get hayPasado(): boolean { return this.desde < this.hoy; }
  get todoFuera(): boolean { return this.desde > this.horizonte; }

  // ── gráfico ──
  readonly W = 1000; readonly H = 250; readonly BASE = 200;
  valor = (t: any): number => (t.tipo === 'pasado' ? (t.g ? t.g.vencia : 0) : t.tipo === 'mes' ? Math.max(t.pasado ? t.pasado.vencia : 0, t.viene ? Math.max(t.viene.firme, t.viene.estimado) : 0) : (t.d ? Math.max(t.d.firme, t.d.estimado) : 0));
  get max(): number { return Math.max(1, ...this.tramos.map(this.valor)); }
  get paso(): number { return this.W / Math.max(1, this.tramos.length); }
  x = (i: number): number => i * this.paso + this.paso * 0.18;
  get ancho(): number { return this.paso * 0.64; }
  alto = (m: number): number => (m > 0 ? Math.max(3, Math.round((m / this.max) * 150)) : 0);
  corto = (m: number): string => (m >= 1000 ? `${Math.round(m / 100) / 10}k` : `${Math.round(m)}`).replace('.', ',');
  pct = (g: any): number | null => (g && g.vencia ? Math.round(g.a_tiempo / g.vencia * 100) : null);
  tono = (g: any): string => (!g || !g.vencia ? '' : g.no_procesado ? 'peligro' : (g.atrasado || g.a_manana) ? 'alerta' : 'exito');
  etiqueta = (t: any): string => (t.tipo === 'mes' ? t.mes.slice(0, 3) : this.dl(t.k));
  get indiceHoy(): number { return this.tramos.findIndex((t) => t.tipo === 'hoy' || (t.tipo === 'mes' && t.k === this.hoy.slice(0, 7))); }

  tocar = (t: any): void => {
    if (t.tipo === 'mes') { this.abrirMes(t.k); return; }
    this.elegido = t.k;
  };
  get tramoElegido(): any {
    const ts = this.tramos.filter((t) => t.tipo !== 'mes');
    return ts.find((t) => t.k === this.elegido) || ts.find((t) => t.tipo === 'hoy') || ts.find((t) => t.d && t.d.firme) || null;
  }
  abrir = (k: string): void => { this.abierto = this.abierto === k ? null : k; };
  partes = (d: any): { lote: number; sin: number } => {
    const tot = (d && d.firme) || 0;
    return tot ? { lote: Math.round((tot - (d.sin_lote || 0)) / tot * 100), sin: Math.round((d.sin_lote || 0) / tot * 100) } : { lote: 0, sin: 0 };
  };
}
